import { Controller } from "@hotwired/stimulus"
import { Calendar } from "fullcalendar"
import interactionPlugin from "fullcalendar/interaction"
import themePlugin from "fullcalendar/themes/classic"
import resourceTimelinePlugin from "fullcalendar-scheduler/resource-timeline"

export default class extends Controller {
  static targets = [
    "templateCalendar",
    "liveCalendar",
    "templateResourcesData",
    "templateEventsData",
    "liveResourcesData",
    "liveEventsData",
    "openingForm",
    "openingMethodInput",
    "openingWeekdayInput",
    "openingStartInput",
    "openingEndInput",
    "openingCommentInput",
    "openingSubmitButton",
    "openingDeleteForm",
    "allocationForm",
    "allocationMethodInput",
    "allocationTenantInput",
    "allocationWeekdayInput",
    "allocationStartInput",
    "allocationEndInput",
    "allocationAreaInput",
    "allocationSpaceInput",
    "allocationCommentInput",
    "allocationSubmitButton",
    "allocationDeleteForm",
    "liveForm",
    "liveMethodInput",
    "liveEntryKindInput",
    "liveTenantInput",
    "liveStartsAtInput",
    "liveEndsAtInput",
    "liveAreaInput",
    "liveSpaceInput",
    "liveSourceKindInput",
    "liveCommentInput",
    "liveSubmitButton",
    "liveDeleteForm",
    "liveDeleteCommentInput"
  ]

  connect() {
    this.templateResources = this.parseJson(this.templateResourcesDataTarget)
    this.templateEvents = this.parseJson(this.templateEventsDataTarget)
    this.liveResources = this.parseJson(this.liveResourcesDataTarget)
    this.liveEvents = this.parseJson(this.liveEventsDataTarget)

    this.buildTemplateCalendar()
    this.buildLiveCalendar()
    this.resetOpeningForm()
    this.resetAllocationForm()
    this.resetLiveForm()
  }

  disconnect() {
    this.templateCalendarInstance?.destroy()
    this.liveCalendarInstance?.destroy()
  }

  resetOpeningForm() {
    this.openingFormTarget.action = this.openingFormTarget.dataset.createUrl
    this.openingMethodInputTarget.value = "post"
    this.openingWeekdayInputTarget.value = "0"
    this.openingStartInputTarget.value = ""
    this.openingEndInputTarget.value = ""
    this.openingCommentInputTarget.value = ""
    this.openingSubmitButtonTarget.textContent = "Save no-access hours"
    this.openingDeleteFormTarget.classList.add("hidden")
  }

  resetAllocationForm() {
    this.allocationFormTarget.action = this.allocationFormTarget.dataset.createUrl
    this.allocationMethodInputTarget.value = "post"
    this.allocationTenantInputTarget.value = ""
    this.allocationWeekdayInputTarget.value = "0"
    this.allocationStartInputTarget.value = ""
    this.allocationEndInputTarget.value = ""
    this.allocationAreaInputTarget.value = ""
    this.allocationSpaceInputTarget.value = ""
    this.allocationCommentInputTarget.value = ""
    this.allocationSubmitButtonTarget.textContent = "Save weekly allocation"
    this.allocationDeleteFormTarget.classList.add("hidden")
    this.filterSpaceOptions(this.allocationAreaInputTarget, this.allocationSpaceInputTarget)
  }

  resetLiveForm() {
    this.liveFormTarget.action = this.liveFormTarget.dataset.createUrl
    this.liveMethodInputTarget.value = "post"
    this.liveEntryKindInputTarget.value = "no_access"
    this.liveTenantInputTarget.value = ""
    this.liveStartsAtInputTarget.value = ""
    this.liveEndsAtInputTarget.value = ""
    this.liveAreaInputTarget.value = ""
    this.liveSpaceInputTarget.value = ""
    this.liveSourceKindInputTarget.value = "manual"
    this.liveCommentInputTarget.value = ""
    this.liveDeleteCommentInputTarget.value = ""
    this.liveSubmitButtonTarget.textContent = "Save live entry"
    this.liveDeleteFormTarget.classList.add("hidden")
    this.liveEntryKindChanged()
    this.filterSpaceOptions(this.liveAreaInputTarget, this.liveSpaceInputTarget)
  }

  allocationAreaChanged() {
    this.filterSpaceOptions(this.allocationAreaInputTarget, this.allocationSpaceInputTarget)
  }

  liveAreaChanged() {
    this.filterSpaceOptions(this.liveAreaInputTarget, this.liveSpaceInputTarget)
  }

  liveEntryKindChanged() {
    const allocation = this.liveEntryKindInputTarget.value === "tenant_allocation"
    this.liveTenantInputTarget.disabled = !allocation
    this.liveAreaInputTarget.disabled = !allocation
    this.liveSpaceInputTarget.disabled = !allocation

    if (!allocation) {
      this.liveTenantInputTarget.value = ""
      this.liveAreaInputTarget.value = ""
      this.liveSpaceInputTarget.value = ""
    }
  }

  buildTemplateCalendar() {
    this.templateCalendarInstance = new Calendar(this.templateCalendarTarget, {
      plugins: [themePlugin, interactionPlugin, resourceTimelinePlugin],
      initialView: "resourceTimelineWeek",
      initialDate: this.startOfCurrentWeek(),
      height: "auto",
      selectable: true,
      resourceColumnsWidth: "16rem",
      slotMinWidth: 52,
      slotDuration: "00:15:00",
      snapDuration: "00:15:00",
      headerToolbar: {
        left: "title",
        center: "",
        right: ""
      },
      resourceColumns: [
        { field: "title", headerContent: "Lane" },
        { field: "targetLabel", headerContent: "Use" }
      ],
      resources: this.templateResources,
      events: this.templateEvents,
      select: (selectionInfo) => this.templateSelectionCreated(selectionInfo),
      eventClick: (clickInfo) => this.templateEventClicked(clickInfo),
      views: {
        resourceTimelineWeek: {
          duration: { weeks: 1 }
        }
      }
    })

    this.templateCalendarInstance.render()
  }

  buildLiveCalendar() {
    this.liveCalendarInstance = new Calendar(this.liveCalendarTarget, {
      plugins: [themePlugin, interactionPlugin, resourceTimelinePlugin],
      initialView: "resourceTimelineWeek",
      height: "auto",
      selectable: true,
      resourceColumnsWidth: "16rem",
      slotMinWidth: 84,
      slotDuration: { hours: 6 },
      snapDuration: "00:15:00",
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth,resourceTimelineYear"
      },
      resourceColumns: [
        { field: "title", headerContent: "Lane" },
        { field: "targetLabel", headerContent: "Use" }
      ],
      resources: this.liveResources,
      events: this.liveEvents,
      select: (selectionInfo) => this.liveSelectionCreated(selectionInfo),
      eventClick: (clickInfo) => this.liveEventClicked(clickInfo),
      views: {
        resourceTimelineDay: { buttonText: "Day" },
        resourceTimelineWeek: { buttonText: "Week" },
        resourceTimelineMonth: { buttonText: "Month" },
        resourceTimelineYear: { buttonText: "Year" }
      }
    })

    this.liveCalendarInstance.render()
  }

  templateSelectionCreated(selectionInfo) {
    const resource = selectionInfo.resource
    const startTime = this.timeString(selectionInfo.start)
    const endTime = this.timeString(selectionInfo.end)
    const weekday = this.mondayWeekday(selectionInfo.start)

    if (resource.id === "no-access") {
      this.resetOpeningForm()
      this.openingWeekdayInputTarget.value = String(weekday)
      this.openingStartInputTarget.value = startTime
      this.openingEndInputTarget.value = endTime
      return
    }

    this.resetAllocationForm()
    this.allocationTenantInputTarget.value = String(resource.extendedProps.tenantId || "")
    this.allocationWeekdayInputTarget.value = String(weekday)
    this.allocationStartInputTarget.value = startTime
    this.allocationEndInputTarget.value = endTime
  }

  templateEventClicked(clickInfo) {
    const event = clickInfo.event
    const props = event.extendedProps

    if (props.editorType === "opening_template") {
      this.resetOpeningForm()
      this.openingFormTarget.action = `${this.openingFormTarget.dataset.updateUrlBase}${props.recordId}`
      this.openingMethodInputTarget.value = "patch"
      this.openingWeekdayInputTarget.value = String(props.weekday)
      this.openingStartInputTarget.value = props.startTime.slice(0, 5)
      this.openingEndInputTarget.value = props.endTime.slice(0, 5)
      this.openingCommentInputTarget.value = props.comment || ""
      this.openingSubmitButtonTarget.textContent = "Update no-access hours"
      this.openingDeleteFormTarget.action = `${this.openingDeleteFormTarget.dataset.urlBase}${props.recordId}`
      this.openingDeleteFormTarget.classList.remove("hidden")
      return
    }

    this.resetAllocationForm()
    this.allocationFormTarget.action = `${this.allocationFormTarget.dataset.updateUrlBase}${props.recordId}`
    this.allocationMethodInputTarget.value = "patch"
    this.allocationTenantInputTarget.value = String(props.tenantId || "")
    this.allocationWeekdayInputTarget.value = String(props.weekday)
    this.allocationStartInputTarget.value = props.startTime.slice(0, 5)
    this.allocationEndInputTarget.value = props.endTime.slice(0, 5)
    this.allocationAreaInputTarget.value = String(props.bookableAreaId || "")
    this.filterSpaceOptions(this.allocationAreaInputTarget, this.allocationSpaceInputTarget)
    this.allocationSpaceInputTarget.value = String(props.bookableSpaceId || "")
    this.allocationCommentInputTarget.value = props.comment || ""
    this.allocationSubmitButtonTarget.textContent = "Update weekly allocation"
    this.allocationDeleteFormTarget.action = `${this.allocationDeleteFormTarget.dataset.urlBase}${props.recordId}`
    this.allocationDeleteFormTarget.classList.remove("hidden")
  }

  liveSelectionCreated(selectionInfo) {
    this.resetLiveForm()
    const resource = selectionInfo.resource

    if (resource.id === "no-access") {
      this.liveEntryKindInputTarget.value = "no_access"
    } else {
      this.liveEntryKindInputTarget.value = "tenant_allocation"
      this.liveTenantInputTarget.value = String(resource.extendedProps.tenantId || "")
    }

    this.liveStartsAtInputTarget.value = this.datetimeLocalValue(selectionInfo.start)
    this.liveEndsAtInputTarget.value = this.datetimeLocalValue(selectionInfo.end)
    this.liveEntryKindChanged()
  }

  liveEventClicked(clickInfo) {
    const event = clickInfo.event
    const props = event.extendedProps

    this.resetLiveForm()
    this.liveFormTarget.action = `${this.liveFormTarget.dataset.updateUrlBase}${props.recordId}`
    this.liveMethodInputTarget.value = "patch"
    this.liveEntryKindInputTarget.value = props.entryKind
    this.liveTenantInputTarget.value = String(props.tenantId || "")
    this.liveStartsAtInputTarget.value = this.datetimeLocalValue(event.start)
    this.liveEndsAtInputTarget.value = this.datetimeLocalValue(event.end)
    this.liveAreaInputTarget.value = String(props.bookableAreaId || "")
    this.filterSpaceOptions(this.liveAreaInputTarget, this.liveSpaceInputTarget)
    this.liveSpaceInputTarget.value = String(props.bookableSpaceId || "")
    this.liveSourceKindInputTarget.value = props.sourceKind || "manual"
    this.liveCommentInputTarget.value = props.comment || ""
    this.liveDeleteCommentInputTarget.value = props.comment || ""
    this.liveSubmitButtonTarget.textContent = "Update live entry"
    this.liveDeleteFormTarget.action = `${this.liveDeleteFormTarget.dataset.urlBase}${props.recordId}`
    this.liveDeleteFormTarget.classList.remove("hidden")
    this.liveEntryKindChanged()
  }

  parseJson(element) {
    try {
      return JSON.parse(element.textContent || "[]")
    } catch (_error) {
      return []
    }
  }

  filterSpaceOptions(areaSelect, spaceSelect) {
    const selectedArea = areaSelect.value

    Array.from(spaceSelect.options).forEach((option) => {
      if (option.value === "") {
        option.hidden = false
        return
      }

      option.hidden = selectedArea !== "" && option.dataset.areaId !== selectedArea
    })

    if (selectedArea !== "" && spaceSelect.selectedOptions[0]?.dataset.areaId !== selectedArea) {
      spaceSelect.value = ""
    }
  }

  timeString(date) {
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
  }

  datetimeLocalValue(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    const hours = String(date.getHours()).padStart(2, "0")
    const minutes = String(date.getMinutes()).padStart(2, "0")
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  mondayWeekday(date) {
    return (date.getDay() + 6) % 7
  }

  startOfCurrentWeek() {
    const today = new Date()
    const clone = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    clone.setDate(clone.getDate() - this.mondayWeekday(clone))
    return clone
  }
}
